import React, { useMemo, useRef } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@hub/popover'
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
} from '@hub/drawer'
import {
  DEFAULT_BREAKPOINT,
  HubResponsiveValue,
  StandardSpacings,
  useBreakpointValue,
} from '@hub/design-system-base'
import { useDisclosure } from '@hub/use-disclosure'
import { Box } from '@hub/box'
import { Stack } from '@hub/stack'
import { ChevronDownSmallIcon, ChevronUpSmallIcon } from '@hub/icon'
import { useOutsideClick } from '@chakra-ui/react'
import ResponsiveButton from './responsive-button'
import { StandardButton } from '../standard-button'

interface ActionSheetProps {
  children: React.ReactNode | null
  onClick?: () => boolean | undefined | void
  onOpen?: () => void
  onClose?: () => void
  isOpen?: boolean
}

type ActionSheetTriggerProps = React.PropsWithChildren<{
  gap?: HubResponsiveValue<StandardSpacings>
}>

export const ActionSheetTrigger: React.FC<ActionSheetTriggerProps> = () => null
export const ActionSheetContent: React.FC<React.PropsWithChildren> = () => null
export const ActionSheetCTA: React.FC<React.PropsWithChildren> = () => null

export const ActionSheet: React.FC<
  React.PropsWithChildren<ActionSheetProps>
> = (props): JSX.Element => {
  const [ctaChild, triggerChildProps, contentChild] = useMemo(() => {
    let triggerChildProps: ActionSheetTriggerProps = {}
    let contentChild: React.ReactElement | null = null
    let ctaChild: React.ReactElement | null = null

    React.Children.forEach(props.children, child => {
      if (!React.isValidElement(child)) {
        return
      }
      if (child.type === ActionSheetTrigger) {
        triggerChildProps = child.props
      }
      if (child.type === ActionSheetContent) {
        contentChild = child.props.children
      }
      if (child.type === ActionSheetCTA) {
        ctaChild = child.props.children
      }
    })
    return [ctaChild, triggerChildProps, contentChild]
  }, [props.children])

  const { onOpen, onClose, isOpen } = useDisclosure()
  const actionSheetRef = useRef<HTMLDivElement>(null)

  const isMobile =
    useBreakpointValue([true, true, false], DEFAULT_BREAKPOINT) ?? false

  const handleClick = (): void => {
    const onClickValue = props.onClick?.()
    if (onClickValue === undefined || onClickValue) {
      toggleOpen()
    }
  }

  const toggleOpen = (): void => {
    if (isOpen) {
      props.onClose?.()
      onClose()
    } else {
      props.onOpen?.()
      onOpen()
    }
  }

  useOutsideClick({
    ref: actionSheetRef,
    handler: onClose,
  })

  return (
    <Box ref={actionSheetRef}>
      <Popover
        isOpen={!isMobile && isOpen}
        onClose={toggleOpen}
        onOpen={toggleOpen}
        placement="bottom-start"
        matchWidth={true}
      >
        <PopoverTrigger>
          <ResponsiveButton>
            <Stack
              direction="row"
              align="center"
              shouldWrapChildren={false}
              gap={triggerChildProps.gap ?? 'spacing-md'}
              sx={{
                flexGrow: '1',
                paddingTop: 'spacing-sm',
                paddingBottom: 'spacing-sm',
                paddingLeft: [0, null, 'spacing-md'],
                paddingRight: [0, null, 'spacing-md'],
              }}
            >
              <Stack
                direction="row"
                textAlign="left"
                alignItems="center"
                shouldWrapChildren={false}
                flexGrow={1}
                gap={triggerChildProps.gap ?? 'spacing-sm'}
              >
                {triggerChildProps.children}
              </Stack>
              {isOpen ? (
                <ChevronUpSmallIcon
                  sx={{
                    flexGrow: 0,
                    display: ['none', null, 'block'],
                  }}
                />
              ) : (
                <ChevronDownSmallIcon
                  sx={{
                    flexGrow: 0,
                    display: ['none', null, 'block'],
                  }}
                />
              )}
            </Stack>
          </ResponsiveButton>
        </PopoverTrigger>
        <PopoverContent
          borderColor="borderSecondary"
          borderRadius={0}
          boxShadow="none !important"
        >
          <Box
            padding="spacing-md"
            sx={{
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
          >
            <Box
              sx={{
                marginBottom: 'spacing-md',
              }}
            >
              {contentChild}
            </Box>
            <StandardButton
              variant="solid"
              onClick={handleClick}
              sx={{ width: '100%' }}
            >
              {ctaChild}
            </StandardButton>
          </Box>
        </PopoverContent>
      </Popover>
      <Drawer
        placement="bottom"
        onClose={toggleOpen}
        isOpen={isOpen && isMobile}
        size="xl"
      >
        <DrawerOverlay sx={{ background: 'rgba(0, 0, 0, 0.6)' }} />
        <DrawerContent>
          <Box style={{ position: 'relative' }}>
            <DrawerCloseButton
              borderColor="borderSecondary"
              borderRadius={0}
              boxShadow="none !important"
              sx={{
                marginX: 'spacing-mobile-margin',
                marginY: 'spacing-md',
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            />
          </Box>
          <DrawerBody
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: 'spacing-2xl',
              paddingTop: 'spacing-3xl',
              paddingLeft: 'spacing-mobile-margin',
              paddingRight: 'spacing-mobile-margin',
              paddingBottom: 'spacing-mobile-margin',
              backgroundColor: 'surfaceBrandPrimary',
            }}
          >
            <Box>{contentChild}</Box>
            <Box>
              <StandardButton variant="solid" onClick={handleClick}>
                {ctaChild}
              </StandardButton>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  )
}
